import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import {
  showSuccessAlert,
  showErrorAlert,
  showInfoAlert,
} from "../../uiInterface/base-ui/alertservice";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";

const ProposalsSettingsLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading Settings...</span>
  </div>
);

const route = all_routes;

type Status = {
  id: string;
  name: string;
  order: number;
};

const initialLifecycle: { name: string; statuses: Status[] } = {
  name: "",
  statuses: [],
};

const ProposalsSettings = () => {
  const initialRef = useRef<{ name: string; statuses: Status[] }>(
    initialLifecycle
  );
  const { lifecycleTypeNames, setLifecycleTypeNames } = useContext(AuthContext);
  const { selectedTenantId } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [lifecycle, setLifecycle] = useState<{
    name: string;
    statuses: Status[];
  }>(initialLifecycle);
  const [lifecycleTypeId, setLifecycleTypeId] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleLifecycleNameChange = (value: string) => {
    setLifecycle((l) => ({ ...l, name: value }));
    setHasUnsavedChanges(true);
  };

  const moveStatus = (sortedIdx: number, direction: number) => {
    const sorted = lifecycle.statuses.slice().sort((a, b) => a.order - b.order);
    const targetIdx = sortedIdx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    [sorted[sortedIdx], sorted[targetIdx]] = [
      sorted[targetIdx],
      sorted[sortedIdx],
    ];
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setLifecycle((l) => ({ ...l, statuses: reOrdered }));
    setHasUnsavedChanges(true);
  };

  const updateStatusName = (idx: number, value: string) => {
    const sorted = lifecycle.statuses.slice().sort((a, b) => a.order - b.order);
    sorted[idx].name = value;
    setLifecycle((l) => ({
      ...l,
      statuses: sorted.map((s, i) => ({ ...s, order: i + 1 })),
    }));
    setHasUnsavedChanges(true);
  };

  const addStatus = () => {
    const newStatus = {
      id: `s${Date.now()}`,
      name: "",
      order: lifecycle.statuses.length + 1,
    };
    setLifecycle((l) => ({ ...l, statuses: [...l.statuses, newStatus] }));
    setHasUnsavedChanges(true);
  };

  const removeStatus = (sortedIdx: number) => {
    const sorted = lifecycle.statuses.slice().sort((a, b) => a.order - b.order);
    sorted.splice(sortedIdx, 1);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setLifecycle((l) => ({ ...l, statuses: reOrdered }));
    setHasUnsavedChanges(true);
  };

  const save = async () => {
    const authHeader = localStorage.getItem("authCred");
    if (!authHeader || !selectedTenantId) {
      console.error("Missing required authentication or tenant ID.");
      return;
    }

    const trimmedName = lifecycle.name.trim();
    const nameChanged = trimmedName !== initialRef.current.name.trim();

    const nonEmpty = lifecycle.statuses.filter((s) => s.name.trim() !== "");
    const sorted = nonEmpty.slice().sort((a, b) => a.order - b.order);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));

    const orders = reOrdered.map((s) => s.order);
    const unique = new Set(orders);
    if (unique.size !== orders.length) {
      await showErrorAlert("Each status must have a unique order number.");
      return;
    }

    const originalIds = new Set(initialRef.current.statuses.map((s) => s.id));
    const currentIds = new Set(reOrdered.map((s) => s.id));

    const deletes = initialRef.current.statuses
      .filter((s) => !currentIds.has(s.id) && !s.id.startsWith("s"))
      .map((s) => ({ proposal_status_id: Number(s.id) }));

    const inserts = reOrdered
      .filter((s) => s.id.startsWith("s") && s.name.trim() !== "")
      .map((s) => ({
        proposal_status_display_name: s.name.trim(),
        proposal_status_order: s.order,
        tenant_id: selectedTenantId,
      }));

    const updates = reOrdered
      .filter((s) => !s.id.startsWith("s"))
      .map((s) => ({
        proposal_status_id: Number(s.id),
        proposal_status_display_name: s.name.trim(),
        proposal_status_order: s.order,
      }));

    try {
      if (nameChanged) {
        if (lifecycleTypeId) {
          const patchRes = await fetch(
            `${API_BASE_URL}/lifecycle-type/${lifecycleTypeId}/`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${authHeader}`,
              },
              body: JSON.stringify({ lifecycle_type_display_name: trimmedName }),
            }
          );

          if (!patchRes.ok) {
            const errorText = await patchRes.text();
            console.error("Lifecycle name update failed:", errorText);
            await showErrorAlert("Error updating lifecycle name.");
            return;
          }
        }
        setLifecycleTypeNames({ ...lifecycleTypeNames, PROPOSAL: trimmedName });
      }

      for (const deleteItem of deletes) {
        const deleteRes = await fetch(
          `${API_BASE_URL}/proposal-status/${deleteItem.proposal_status_id}/`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Basic ${authHeader}`,
            },
          }
        );
        if (!deleteRes.ok) {
          console.error("Delete failed:", await deleteRes.text());
          await showErrorAlert("Error deleting proposal status.");
          return;
        }
      }

      for (const insertItem of inserts) {
        const insertRes = await fetch(`${API_BASE_URL}/proposal-status/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
          body: JSON.stringify(insertItem),
        });
        if (!insertRes.ok) {
          console.error("Insert failed:", await insertRes.text());
          await showErrorAlert("Error creating proposal status.");
          return;
        }
      }

      for (const updateItem of updates) {
        const updateRes = await fetch(
          `${API_BASE_URL}/proposal-status/${updateItem.proposal_status_id}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
            body: JSON.stringify({
              proposal_status_display_name:
                updateItem.proposal_status_display_name,
              proposal_status_order: updateItem.proposal_status_order,
            }),
          }
        );
        if (!updateRes.ok) {
          console.error("Update failed:", await updateRes.text());
          await showErrorAlert("Error updating proposal status.");
          return;
        }
      }

      let successMessage = "";
      if (nameChanged) {
        successMessage = "Lifecycle name and proposal statuses updated successfully.";
      } else {
        successMessage = "Proposal statuses updated successfully.";
      }
      await showSuccessAlert(successMessage);
      setLifecycle((l) => ({ ...l, statuses: reOrdered }));
      initialRef.current = { name: trimmedName, statuses: reOrdered };
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Save error:", error);
      await showErrorAlert("An error occurred while saving.");
    }
  };

  const cancel = () => {
    setLifecycle(initialRef.current);
    setHasUnsavedChanges(false);
  };

  const hasChanges = useMemo(() => {
    const trimmedName = lifecycle.name.trim();
    const nameChanged = trimmedName !== initialRef.current.name.trim();

    const nonEmpty = lifecycle.statuses.filter((s) => s.name.trim() !== "");
    const sorted = nonEmpty.slice().sort((a, b) => a.order - b.order);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));

    const originalIds = new Set(initialRef.current.statuses.map((s) => s.id));
    const currentIds = new Set(reOrdered.map((s) => s.id));

    const deletes = initialRef.current.statuses.filter(
      (s) => !currentIds.has(s.id) && !s.id.startsWith("s")
    );
    const inserts = reOrdered.filter(
      (s) => s.id.startsWith("s") && s.name.trim() !== ""
    );
    const updates = reOrdered.filter(
      (s) => originalIds.has(s.id) && !s.id.startsWith("s")
    );

    return (
      nameChanged ||
      inserts.length > 0 ||
      updates.length > 0 ||
      deletes.length > 0 ||
      hasUnsavedChanges
    );
  }, [lifecycle, hasUnsavedChanges]);

  useEffect(() => {
    if (!selectedTenantId) return;
    const load = async () => {
      const authHeader = localStorage.getItem("authCred");
      if (!authHeader) return;

      setIsLoading(true);
      try {
        let name = lifecycleTypeNames.PROPOSAL || "Proposal";
        try {
          const qt = await fetch(
            `${API_BASE_URL}/lifecycle-type/?lifecycle_type_key=PROPOSAL&tenant_id=${selectedTenantId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${authHeader}`,
              },
            }
          );
          const qj = await qt.json();
          const lt = qj.results?.[0];
          if (lt) {
            setLifecycleTypeId(lt.lifecycle_type_id);
            name = lt.lifecycle_type_display_name || name;
          }
        } catch (err) {
          console.error("Error loading proposal lifecycle name:", err);
        }

        const response = await fetch(
          `${API_BASE_URL}/proposal-status/?tenant_id=${selectedTenantId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );
        const data = await response.json();
        const formatted = data.results
          .sort(
            (a: any, b: any) =>
              a.proposal_status_order - b.proposal_status_order
          )
          .map((s: any) => ({
            id: String(s.proposal_status_id),
            name: s.proposal_status_display_name,
            order: s.proposal_status_order,
          }));

        setLifecycle({ name, statuses: formatted });
        initialRef.current = { name: name.trim(), statuses: formatted };
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error("Error loading proposals:", err);
        await showErrorAlert("Failed to load proposal settings.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [selectedTenantId]);

  const sortedStatuses = lifecycle.statuses
    .slice()
    .sort((a, b) => a.order - b.order);

  const userRole = useTenantRole(selectedTenantId || 0);

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="lifecycle"
                    variant="bottom"
                    includeIcons={true}
                    context="organization"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <h4 className="fw-semibold mb-3">LifeCycle Settings</h4>
                        <div className="list-group list-group-flush settings-sidebar">
                          <Link to={route.leadssettings} className="fw-medium">
                            Leads
                          </Link>
                          <Link
                            to={route.pipelinesettings}
                            className="fw-medium"
                          >
                            Pipelines
                          </Link>
                          <Link to={route.dealssettings} className="fw-medium">
                            Deals
                          </Link>
                          <Link
                            to={route.proposalssettings}
                            className="fw-medium active"
                          >
                            Proposals
                          </Link>
                          <Link
                            to={route.contractssettings}
                            className="fw-medium"
                          >
                            Contracts
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-9 col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      {isLoading ? (
                        <ProposalsSettingsLoader />
                      ) : (
                        <>
                          <div className="border-bottom mb-3 pb-3">
                            <h4 className="fw-semibold mb-1">
                              {lifecycle.name || "Proposal"} Settings
                            </h4>
                            <p>
                              Configure the{" "}
                              {lifecycle.name.toLowerCase() || "proposal"}{" "}
                              information below
                            </p>
                          </div>
                          <div className="mb-4">
                            <label className="form-label fw-medium">
                              LifeCycle Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              disabled={userRole?.role_key === "SALESUSER"}
                              value={lifecycle.name}
                              onChange={(e) =>
                                handleLifecycleNameChange(e.target.value)
                              }
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-medium">
                              LifeCycle Statuses
                            </label>
                          </div>
                          {sortedStatuses.map((status, idx) => (
                            <div
                              key={status.id}
                              className="d-flex align-items-center mb-2 p-2 border rounded w-20"
                            >
                              <input
                                type="text"
                                className="form-control me-3"
                                placeholder="Status name"
                                disabled={userRole?.role_key === "SALESUSER"}
                                value={status.name}
                                onChange={(e) =>
                                  updateStatusName(idx, e.target.value)
                                }
                              />
                              {userRole?.role_key === "CLIENTADMIN" &&
                                "SUPERADMIN" &&
                                "PLATFORM_ANALYST" && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary me-1"
                                      onClick={(e) => {
                                        moveStatus(idx, -1);
                                        e.currentTarget.blur();
                                      }}
                                      disabled={idx === 0}
                                    >
                                      ▲
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary me-3"
                                      onClick={(e) => {
                                        moveStatus(idx, 1);
                                        e.currentTarget.blur();
                                      }}
                                      disabled={
                                        idx === sortedStatuses.length - 1
                                      }
                                    >
                                      ▼
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={(e) => {
                                        removeStatus(idx);
                                        e.currentTarget.blur();
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </>
                                )}
                            </div>
                          ))}
                          {userRole?.role_key === "CLIENTADMIN" &&
                            "SUPERADMIN" &&
                            "PLATFORM_ANALYST" && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm mt-2 mb-4"
                                  onClick={(e) => {
                                    addStatus();
                                    e.currentTarget.blur();
                                  }}
                                >
                                  <i className="fa fa-plus" /> Add Status
                                </button>
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary me-2"
                                    onClick={(e) => {
                                      cancel();
                                      e.currentTarget.blur();
                                    }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary me-2"
                                    onClick={(e) => {
                                      save();
                                      e.currentTarget.blur();
                                    }}
                                  >
                                    Save Changes
                                  </button>
                                </div>
                              </>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalsSettings;
